import React, { useEffect } from 'react';
import cn from 'classnames';
import { useDidMount } from '../../../../../../../../../components/common/effects';
import { Visibility } from '../../../../../../../../../types/common-types';
import './style.scss';

interface CalculatorValue {
  amount: string;
  duration: string;
}
interface Labels {
  header: string;
  loanAmount: string;
  loanDuration: string;
  selectLoanAmount: string;
  selectLoanDuration: string;
  month: string;
  headerInfo: string;
  body: string;
}
interface Settings {
  amount: { min: number; max: number; step: number };
  duration: { min: number; max: number; step: number };
}
interface Props {
  id: string;
  visibility?: Visibility;
  value: CalculatorValue;
  extra: {
    labels: Labels;
    settings: Settings;
    defaultEditMode: boolean;
  };
  onSubmit: (id: string) => void;
  onChange: (
    value: {
      id: string;
      value: object;
      error: boolean;
    },
    callback?: () => void
  ) => void;
}

const Calculator: React.FC<Props> = ({
  id,
  visibility,
  extra,
  value,
  onChange,
  onSubmit,
}) => {
  const { labels, settings } = extra;
  const {
    header,
    loanAmount,
    loanDuration,
    selectLoanAmount,
    selectLoanDuration,
    month,
    headerInfo,
    body,
  } = labels;
  const { amount: settingsAmount, duration: settingsDuration } = settings;
  const { amount, duration } = value;
  const didMount = useDidMount();
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (didMount) onSubmit(id);
    }, 500);
    return () => clearTimeout(timeout);
  }, [value]);

  const handleChange = (key: string, val: string) => {
    const newData = { ...value, [key]: val };
    onChange({ id, value: newData, error: false });
  };

  return (
    <div
      id={id}
      data-cy="calculator"
      styleName={cn('calculator', {
        hidden: visibility === Visibility.Hidden,
      })}
    >
      <div styleName="calculator__header">
        <span>{header}</span>
      </div>
      <div styleName="calculator__divider" />
      <div styleName="calculator__editor">
        <div styleName="calculator__editor__item">
          <div styleName="calculator__editor__header">
            <span styleName="calculator__editor__title">
              {selectLoanAmount}
            </span>
            <span styleName="calculator__editor__value">
              {`${amount} `}
              &euro;
            </span>
          </div>
          <div styleName="calculator__editor__slider">
            <input
              type="range"
              data-cy-prop="value.amount"
              min={settingsAmount.min}
              max={settingsAmount.max}
              step={settingsAmount.step}
              value={amount}
              onChange={e => handleChange('amount', e.target.value)}
            />
          </div>
          <div styleName="calculator__editor__footer">
            <span>
              {`${settingsAmount.min} `}
              &euro;
            </span>
            <span>
              {`${settingsAmount.max} `}
              &euro;
            </span>
          </div>
        </div>
        <div styleName="calculator__editor__item">
          <div styleName="calculator__editor__header">
            <span styleName="calculator__editor__title">
              {selectLoanDuration}
            </span>
            <span styleName="calculator__editor__value">
              <b>{`${duration}`}</b>
              &nbsp;
              {`${month}`}
            </span>
          </div>
          <div styleName="calculator__editor__slider">
            <input
              type="range"
              data-cy-prop="value.duration"
              min={settingsDuration.min}
              max={settingsDuration.max}
              step={settingsDuration.step}
              value={duration}
              onChange={e => handleChange('duration', e.target.value)}
            />
          </div>
          <div styleName="calculator__editor__footer">
            <span>{`${settingsDuration.min}`}</span>
            <span>{`${settingsDuration.max} ${month}`}</span>
          </div>
        </div>
        {body ? (
          <div styleName="calculator__body">
            <div>{body}</div>
          </div>
        ) : null}
        <div styleName="calculator__example">
          <div styleName="calculator__example__row header">
            <b>{headerInfo}</b>
          </div>
          <div styleName="calculator__example__row">
            <div styleName="calculator__example__tbl">
              <span>{loanAmount}</span>
              <span styleName="calculator__example__tbl__val">
                <b>
                  {`${amount} `}
                  &euro;
                </b>
              </span>
            </div>
            <div styleName="calculator__example__tbl">
              <span>{loanDuration}</span>
              <span styleName="calculator__example__tbl__val">
                <b>
                  {`${duration} `}
                  {month}
                </b>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
